// src/pages/trading-portal/MarketDataIngestion/MarketDataIngestionDashboard.js
// Market Data Ingestion dashboard overview

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Grid,
    Card,
    CardContent,
    Paper,
    CircularProgress,
    Alert,
    Button,
    Chip,
    Skeleton,
} from '@mui/material';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import CurrencyBitcoinIcon from '@mui/icons-material/CurrencyBitcoin';
import WaterIcon from '@mui/icons-material/Water';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import axiosInstance from '../../../utils/axiosInstance';
import { hasPermission } from '../../../utils/permissionUtils';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#f59e0b';

// Sub-components
const StatCard = ({ icon: Icon, title, value, color, subtitle, onClick }) => (
    <Card
        sx={{
            height: '100%',
            borderRadius: '16px',
            border: '1px solid #e2e8f0',
            boxShadow: 'none',
            transition: 'all 0.2s ease',
            cursor: onClick ? 'pointer' : 'default',
            '&:hover': onClick
                ? { borderColor: color, boxShadow: `0 4px 20px ${color}20`, transform: 'translateY(-2px)' }
                : {},
        }}
        onClick={onClick}
    >
        <CardContent sx={{ p: 2.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box
                    sx={{
                        p: 1.5,
                        borderRadius: '14px',
                        bgcolor: `${color}15`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <Icon sx={{ color, fontSize: 28 }} />
                </Box>
                <Box sx={{ flex: 1 }}>
                    <Typography
                        variant="body2"
                        sx={{ fontFamily, color: '#64748b', fontWeight: 500, fontSize: '0.8rem' }}
                    >
                        {title}
                    </Typography>
                    <Typography
                        variant="h5"
                        sx={{ fontFamily, fontWeight: 700, color: '#1e293b', lineHeight: 1.2 }}
                    >
                        {value}
                    </Typography>
                    {subtitle && (
                        <Typography
                            variant="caption"
                            sx={{ fontFamily, color: '#94a3b8', fontWeight: 500 }}
                        >
                            {subtitle}
                        </Typography>
                    )}
                </Box>
            </Box>
        </CardContent>
    </Card>
);

const StatCardSkeleton = () => (
    <Card sx={{ height: '100%', borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
        <CardContent sx={{ p: 2.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Skeleton variant="rounded" width={52} height={52} sx={{ borderRadius: '14px' }} />
                <Box sx={{ flex: 1 }}>
                    <Skeleton variant="text" width="60%" sx={{ fontSize: '0.8rem' }} />
                    <Skeleton variant="text" width="40%" sx={{ fontSize: '1.5rem' }} />
                </Box>
            </Box>
        </CardContent>
    </Card>
);

const NAV_ITEMS = [
    { label: 'Stock Price Feed', icon: ShowChartIcon, path: '/trading-portal/market-data-ingestion/price-feed', color: ACCENT, description: 'Real-time and historical stock price data' },
    { label: 'Options Chain', icon: AccountTreeIcon, path: '/trading-portal/market-data-ingestion/options-chain', color: '#8b5cf6', description: 'Options chain configurations and data' },
    { label: 'Macro Economic', icon: AccountBalanceIcon, path: '/trading-portal/market-data-ingestion/macro-economic', color: '#3b82f6', description: 'Macro economic indicators and monitors' },
    { label: 'BTC Correlation', icon: CurrencyBitcoinIcon, path: '/trading-portal/market-data-ingestion/btc-correlation', color: '#f97316', description: 'Bitcoin price and correlation tracking' },
    { label: 'Liquidity Engine', icon: WaterIcon, path: '/trading-portal/market-data-ingestion/liquidity-engine', color: '#06b6d4', description: 'Net liquidity calculations and tracking' },
    { label: 'Data Quality', icon: VerifiedUserIcon, path: '/trading-portal/market-data-ingestion/data-quality', color: '#10b981', description: 'Data quality rules and monitoring' },
];

export default function MarketDataIngestionDashboard() {
    const navigate = useNavigate();

    const [canView, setCanView] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [dashboardData, setDashboardData] = useState({
        active_price_feeds: 0,
        total_price_feeds: 0,
        active_options_configs: 0,
        total_options_configs: 0,
        active_macro_monitors: 0,
        latest_btc_price: null,
        latest_net_liquidity: null,
        active_quality_configs: 0,
        upcoming_corporate_actions: [],
        recent_divergences: [],
    });

    useEffect(() => {
        const checkPerms = async () => {
            const view = await hasPermission('tr-mdi-dashboard', 'view');
            setCanView(view);
        };
        checkPerms();
    }, []);

    const fetchDashboard = useCallback(async () => {
        if (!canView) return;
        setLoading(true);
        setError('');
        try {
            const response = await axiosInstance.get('/api/market-data-ingestion/dashboard');
            const data = response.data?.data || response.data || {};
            setDashboardData({
                active_price_feeds: data.active_price_feeds || 0,
                total_price_feeds: data.total_price_feeds || 0,
                active_options_configs: data.active_options_configs || 0,
                total_options_configs: data.total_options_configs || 0,
                active_macro_monitors: data.active_macro_monitors || 0,
                latest_btc_price: data.latest_btc_price || null,
                latest_net_liquidity: data.latest_net_liquidity || null,
                active_quality_configs: data.active_quality_configs || 0,
                upcoming_corporate_actions: data.upcoming_corporate_actions || [],
                recent_divergences: data.recent_divergences || [],
            });
        } catch (err) {
            console.error('Failed to fetch market data ingestion dashboard:', err);
            setError(err.response?.data?.message || 'Failed to load dashboard data.');
        } finally {
            setLoading(false);
        }
    }, [canView]);

    useEffect(() => {
        if (canView) {
            fetchDashboard();
        }
    }, [fetchDashboard, canView]);

    const formatCurrency = (value) => {
        if (value == null) return '--';
        return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value);
    };

    const formatLargeNumber = (value) => {
        if (value == null) return '--';
        const absVal = Math.abs(value);
        if (absVal >= 1e12) return `$${(value / 1e12).toFixed(2)}T`;
        if (absVal >= 1e9) return `$${(value / 1e9).toFixed(2)}B`;
        if (absVal >= 1e6) return `$${(value / 1e6).toFixed(2)}M`;
        return formatCurrency(value);
    };

    const renderChangeIndicator = (change, isPercent = false) => {
        if (change == null) return null;
        const isPositive = change >= 0;
        return (
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}>
                {isPositive ? (
                    <ArrowUpwardIcon sx={{ fontSize: 14, color: '#10b981' }} />
                ) : (
                    <ArrowDownwardIcon sx={{ fontSize: 14, color: '#ef4444' }} />
                )}
                <Typography
                    component="span"
                    sx={{
                        fontFamily,
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        color: isPositive ? '#10b981' : '#ef4444',
                    }}
                >
                    {isPercent ? `${Math.abs(change).toFixed(2)}%` : formatCurrency(Math.abs(change))}
                </Typography>
            </Box>
        );
    };

    if (!canView) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error">You do not have permission to view the Market Data Ingestion Dashboard.</Alert>
            </Box>
        );
    }

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
            {/* Welcome Header with Gradient */}
            <Paper
                elevation={0}
                sx={{
                    background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                    borderRadius: { xs: 0, md: '0 0 24px 24px' },
                    px: { xs: 2.5, md: 4 },
                    py: { xs: 3, md: 4 },
                    mb: 3,
                    position: 'relative',
                    overflow: 'hidden',
                }}
            >
                {/* Decorative circles */}
                <Box sx={{ position: 'absolute', top: -30, right: -30, width: 160, height: 160, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.06)' }} />
                <Box sx={{ position: 'absolute', bottom: -40, right: 80, width: 100, height: 100, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.04)' }} />

                <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', md: 'center' }, gap: 2 }}>
                    <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                            <Box sx={{ p: 1.5, borderRadius: '14px', bgcolor: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <CloudDownloadIcon sx={{ fontSize: 28, color: '#fff' }} />
                            </Box>
                            <Box>
                                <Typography variant="h4" sx={{ fontFamily, fontWeight: 800, color: 'white', fontSize: { xs: '1.4rem', md: '1.85rem' }, lineHeight: 1.2 }}>
                                    Market Data Ingestion
                                </Typography>
                                <Typography sx={{ fontFamily, color: 'rgba(255,255,255,0.8)', fontSize: '0.9rem', mt: 0.25 }}>
                                    Ingest, normalize, and store real-time and historical market data
                                </Typography>
                            </Box>
                        </Box>
                    </Box>
                    {/* Active Feeds Status */}
                    <Box sx={{ bgcolor: 'rgba(255,255,255,0.15)', borderRadius: '14px', px: 2.5, py: 1.5, textAlign: 'center', backdropFilter: 'blur(10px)', border: '1px solid rgba(255,255,255,0.15)' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, justifyContent: 'center' }}>
                            <TrendingUpIcon sx={{ fontSize: 18, color: '#fff' }} />
                            <Typography sx={{ fontFamily, fontWeight: 800, color: '#fff', fontSize: '1.1rem', lineHeight: 1 }}>
                                {loading ? '--' : `${dashboardData.active_price_feeds} Active`}
                            </Typography>
                        </Box>
                        <Typography sx={{ fontFamily, color: 'rgba(255,255,255,0.7)', fontSize: '0.7rem', fontWeight: 500, mt: 0.25 }}>
                            Price Feeds
                        </Typography>
                    </Box>
                </Box>
            </Paper>

            <Box sx={{ px: { xs: 2, md: 4 }, pb: 4, maxWidth: 1400, mx: 'auto' }}>
                {/* Error Alert */}
                {error && (
                    <Alert severity="error" sx={{ mb: 3, borderRadius: '12px', fontFamily }} action={
                        <Button color="inherit" size="small" onClick={fetchDashboard} sx={{ fontFamily, textTransform: 'none', fontWeight: 600 }}>
                            Retry
                        </Button>
                    }>
                        {error}
                    </Alert>
                )}

                {/* Stats Grid */}
                <Grid container spacing={2.5} sx={{ mb: 4 }}>
                    <Grid item xs={12} sm={6} md={4}>
                        {loading ? <StatCardSkeleton /> : (
                            <StatCard
                                icon={ShowChartIcon}
                                title="Active Price Feeds"
                                value={`${dashboardData.active_price_feeds} / ${dashboardData.total_price_feeds}`}
                                color={ACCENT}
                                subtitle="Configured price feeds"
                                onClick={() => navigate('/trading-portal/market-data-ingestion/price-feed')}
                            />
                        )}
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        {loading ? <StatCardSkeleton /> : (
                            <StatCard
                                icon={AccountTreeIcon}
                                title="Options Chain Configs"
                                value={`${dashboardData.active_options_configs} / ${dashboardData.total_options_configs}`}
                                color="#8b5cf6"
                                subtitle="Active chain configurations"
                                onClick={() => navigate('/trading-portal/market-data-ingestion/options-chain')}
                            />
                        )}
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        {loading ? <StatCardSkeleton /> : (
                            <StatCard
                                icon={AccountBalanceIcon}
                                title="Macro Monitors Active"
                                value={dashboardData.active_macro_monitors}
                                color="#3b82f6"
                                subtitle="Economic indicators tracked"
                                onClick={() => navigate('/trading-portal/market-data-ingestion/macro-economic')}
                            />
                        )}
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        {loading ? <StatCardSkeleton /> : (
                            <StatCard
                                icon={CurrencyBitcoinIcon}
                                title="BTC Price"
                                value={dashboardData.latest_btc_price?.price != null ? formatCurrency(dashboardData.latest_btc_price.price) : '--'}
                                color="#f97316"
                                subtitle={
                                    dashboardData.latest_btc_price?.change_24h != null ? (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <Typography component="span" sx={{ fontFamily, color: '#94a3b8', fontWeight: 500, fontSize: '0.75rem' }}>
                                                24h:
                                            </Typography>
                                            {renderChangeIndicator(dashboardData.latest_btc_price.change_24h, true)}
                                        </Box>
                                    ) : '24h change unavailable'
                                }
                                onClick={() => navigate('/trading-portal/market-data-ingestion/btc-correlation')}
                            />
                        )}
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        {loading ? <StatCardSkeleton /> : (
                            <StatCard
                                icon={WaterIcon}
                                title="Net Liquidity"
                                value={dashboardData.latest_net_liquidity?.net_liquidity != null ? formatLargeNumber(dashboardData.latest_net_liquidity.net_liquidity) : '--'}
                                color="#06b6d4"
                                subtitle={
                                    dashboardData.latest_net_liquidity?.change_percent != null ? (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <Typography component="span" sx={{ fontFamily, color: '#94a3b8', fontWeight: 500, fontSize: '0.75rem' }}>
                                                Change:
                                            </Typography>
                                            {renderChangeIndicator(dashboardData.latest_net_liquidity.change_percent, true)}
                                        </Box>
                                    ) : 'Change unavailable'
                                }
                                onClick={() => navigate('/trading-portal/market-data-ingestion/liquidity-engine')}
                            />
                        )}
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        {loading ? <StatCardSkeleton /> : (
                            <StatCard
                                icon={VerifiedUserIcon}
                                title="Data Quality Configs"
                                value={dashboardData.active_quality_configs}
                                color="#10b981"
                                subtitle="Active quality rules"
                                onClick={() => navigate('/trading-portal/market-data-ingestion/data-quality')}
                            />
                        )}
                    </Grid>
                </Grid>

                {/* Quick Navigation */}
                <Box sx={{ mb: 4 }}>
                    <Typography sx={{ fontFamily, fontWeight: 700, color: '#1e293b', fontSize: '1.1rem', mb: 2 }}>
                        Sub-Modules
                    </Typography>
                    <Grid container spacing={2}>
                        {NAV_ITEMS.map((item) => (
                            <Grid item xs={12} sm={6} md={4} key={item.path}>
                                <Card
                                    sx={{
                                        borderRadius: '16px',
                                        border: '1px solid #e2e8f0',
                                        boxShadow: 'none',
                                        transition: 'all 0.2s ease',
                                        cursor: 'pointer',
                                        '&:hover': {
                                            borderColor: item.color,
                                            boxShadow: `0 4px 20px ${item.color}20`,
                                            transform: 'translateY(-2px)',
                                        },
                                    }}
                                    onClick={() => navigate(item.path)}
                                >
                                    <CardContent sx={{ p: 2.5 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                <Box
                                                    sx={{
                                                        p: 1.5,
                                                        borderRadius: '14px',
                                                        bgcolor: `${item.color}15`,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                    }}
                                                >
                                                    <item.icon sx={{ color: item.color, fontSize: 24 }} />
                                                </Box>
                                                <Box>
                                                    <Typography sx={{ fontFamily, fontWeight: 700, color: '#1e293b', fontSize: '0.9rem' }}>
                                                        {item.label}
                                                    </Typography>
                                                    <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.78rem', fontWeight: 500 }}>
                                                        {item.description}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                            <NavigateNextIcon sx={{ color: '#cbd5e1', fontSize: 22 }} />
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                </Box>

                {/* Bottom Grid: Corporate Actions + Recent Divergences */}
                <Grid container spacing={3}>
                    {/* Upcoming Corporate Actions */}
                    <Grid item xs={12} md={6}>
                        <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none', height: '100%' }}>
                            <CardContent sx={{ p: 0 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2.5, pt: 2.5, pb: 1.5 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <AccountBalanceIcon sx={{ color: '#3b82f6', fontSize: 22 }} />
                                        <Typography sx={{ fontFamily, fontWeight: 700, color: '#1e293b', fontSize: '1rem' }}>
                                            Upcoming Corporate Actions
                                        </Typography>
                                    </Box>
                                    <Button
                                        size="small"
                                        onClick={() => navigate('/trading-portal/market-data-ingestion/price-feed')}
                                        sx={{ fontFamily, fontWeight: 600, textTransform: 'none', color: ACCENT, fontSize: '0.78rem', '&:hover': { bgcolor: `${ACCENT}08` } }}
                                    >
                                        View All
                                    </Button>
                                </Box>

                                <Box sx={{ px: 2, pb: 2 }}>
                                    {loading ? (
                                        Array.from({ length: 3 }).map((_, i) => (
                                            <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1.5, px: 0.5 }}>
                                                <Skeleton variant="circular" width={42} height={42} />
                                                <Box sx={{ flex: 1 }}>
                                                    <Skeleton variant="text" width="70%" sx={{ fontSize: '0.85rem' }} />
                                                    <Skeleton variant="text" width="40%" sx={{ fontSize: '0.7rem' }} />
                                                </Box>
                                            </Box>
                                        ))
                                    ) : dashboardData.upcoming_corporate_actions.length === 0 ? (
                                        <Box sx={{ py: 4, textAlign: 'center' }}>
                                            <AccountBalanceIcon sx={{ fontSize: 40, color: '#e2e8f0', mb: 1 }} />
                                            <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.85rem', fontWeight: 500 }}>
                                                No upcoming corporate actions
                                            </Typography>
                                            <Typography sx={{ fontFamily, color: '#cbd5e1', fontSize: '0.78rem', mt: 0.5 }}>
                                                Corporate actions will appear here when detected
                                            </Typography>
                                        </Box>
                                    ) : (
                                        dashboardData.upcoming_corporate_actions.slice(0, 5).map((action, index) => (
                                            <Box
                                                key={action.id || index}
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 2,
                                                    py: 1.5,
                                                    px: 0.5,
                                                    borderBottom: '1px solid #f1f5f9',
                                                    '&:last-child': { borderBottom: 'none' },
                                                }}
                                            >
                                                <Box sx={{
                                                    width: 42, height: 42, borderRadius: '10px',
                                                    bgcolor: '#dbeafe',
                                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                }}>
                                                    <AccountBalanceIcon sx={{ fontSize: 22, color: '#3b82f6' }} />
                                                </Box>
                                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                                    <Typography sx={{ fontFamily, fontWeight: 600, color: '#1e293b', fontSize: '0.85rem' }}>
                                                        {action.symbol || 'Unknown'} - {action.action_type || action.type || 'Action'}
                                                    </Typography>
                                                    <Typography variant="caption" sx={{ fontFamily, color: '#94a3b8' }}>
                                                        {action.date ? new Date(action.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : action.description || 'No details'}
                                                    </Typography>
                                                </Box>
                                                <Chip
                                                    label={action.status || 'Upcoming'}
                                                    size="small"
                                                    sx={{
                                                        fontFamily,
                                                        fontWeight: 600,
                                                        fontSize: '0.7rem',
                                                        height: 24,
                                                        bgcolor: '#dbeafe',
                                                        color: '#3b82f6',
                                                    }}
                                                />
                                            </Box>
                                        ))
                                    )}
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Recent Divergences */}
                    <Grid item xs={12} md={6}>
                        <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none', height: '100%' }}>
                            <CardContent sx={{ p: 0 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2.5, pt: 2.5, pb: 1.5 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <TrendingUpIcon sx={{ color: '#f97316', fontSize: 22 }} />
                                        <Typography sx={{ fontFamily, fontWeight: 700, color: '#1e293b', fontSize: '1rem' }}>
                                            Recent Divergences
                                        </Typography>
                                    </Box>
                                    <Button
                                        size="small"
                                        onClick={() => navigate('/trading-portal/market-data-ingestion/btc-correlation')}
                                        sx={{ fontFamily, fontWeight: 600, textTransform: 'none', color: ACCENT, fontSize: '0.78rem', '&:hover': { bgcolor: `${ACCENT}08` } }}
                                    >
                                        View All
                                    </Button>
                                </Box>

                                <Box sx={{ px: 2, pb: 2 }}>
                                    {loading ? (
                                        Array.from({ length: 3 }).map((_, i) => (
                                            <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1.5, px: 0.5 }}>
                                                <Skeleton variant="circular" width={42} height={42} />
                                                <Box sx={{ flex: 1 }}>
                                                    <Skeleton variant="text" width="70%" sx={{ fontSize: '0.85rem' }} />
                                                    <Skeleton variant="text" width="40%" sx={{ fontSize: '0.7rem' }} />
                                                </Box>
                                            </Box>
                                        ))
                                    ) : dashboardData.recent_divergences.length === 0 ? (
                                        <Box sx={{ py: 4, textAlign: 'center' }}>
                                            <TrendingUpIcon sx={{ fontSize: 40, color: '#e2e8f0', mb: 1 }} />
                                            <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.85rem', fontWeight: 500 }}>
                                                No recent divergences
                                            </Typography>
                                            <Typography sx={{ fontFamily, color: '#cbd5e1', fontSize: '0.78rem', mt: 0.5 }}>
                                                BTC-stock divergences will appear here when detected
                                            </Typography>
                                        </Box>
                                    ) : (
                                        dashboardData.recent_divergences.slice(0, 5).map((divergence, index) => (
                                            <Box
                                                key={divergence.id || index}
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 2,
                                                    py: 1.5,
                                                    px: 0.5,
                                                    borderBottom: '1px solid #f1f5f9',
                                                    '&:last-child': { borderBottom: 'none' },
                                                }}
                                            >
                                                <Box sx={{
                                                    width: 42, height: 42, borderRadius: '10px',
                                                    bgcolor: divergence.direction === 'BULLISH' || divergence.direction === 'UP' ? '#dcfce7' : '#fee2e2',
                                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                }}>
                                                    {divergence.direction === 'BULLISH' || divergence.direction === 'UP' ? (
                                                        <ArrowUpwardIcon sx={{ fontSize: 22, color: '#10b981' }} />
                                                    ) : (
                                                        <ArrowDownwardIcon sx={{ fontSize: 22, color: '#ef4444' }} />
                                                    )}
                                                </Box>
                                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                                    <Typography sx={{ fontFamily, fontWeight: 600, color: '#1e293b', fontSize: '0.85rem' }}>
                                                        {divergence.symbol || 'Unknown'} vs BTC
                                                    </Typography>
                                                    <Typography variant="caption" sx={{ fontFamily, color: '#94a3b8' }}>
                                                        {divergence.description || `Divergence: ${divergence.magnitude ? divergence.magnitude.toFixed(2) + '%' : 'N/A'}`}
                                                    </Typography>
                                                </Box>
                                                <Chip
                                                    label={divergence.severity || divergence.direction || 'Info'}
                                                    size="small"
                                                    sx={{
                                                        fontFamily,
                                                        fontWeight: 600,
                                                        fontSize: '0.7rem',
                                                        height: 24,
                                                        bgcolor: divergence.direction === 'BULLISH' || divergence.direction === 'UP' ? '#dcfce7' : '#fee2e2',
                                                        color: divergence.direction === 'BULLISH' || divergence.direction === 'UP' ? '#10b981' : '#ef4444',
                                                    }}
                                                />
                                            </Box>
                                        ))
                                    )}
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </Box>
        </Box>
    );
}
